import { useState, type Dispatch, type SetStateAction } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ContextFileUploader } from './context-file-uploader';
import { Textarea } from '../../../../components/shadcn/textarea';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useFormatContext } from '@/api/mutations/useFormatContext';
import { useGetSuggestedContextQuestions } from '@/api/queries/useGetSuggestedContextQuestions';
import { Icon } from '@/new/components/icon';
import { IssuesList, type IssueFormItem } from '@/new/components/matter/issues-list';
import { PartiesList, type PartyFormItem } from '@/new/components/matter/parties-list';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Input } from '@/new/components/shadcn/input';

export interface ExtractionCounts {
  issues: number;
  parties: number;
  suggestions: number;
}

interface MatterCreatorIssuesAndContextProps {
  hasContextFiles: boolean;
  onSkipUpload: () => void;
  onShowUpload: () => void;
  onFilesSelected: (files: File[]) => void;
  isUploading: boolean;
  extractionCounts: ExtractionCounts;
  showSuggestedQuestions: boolean;
  additionalContext: string;
  setAdditionalContext: Dispatch<SetStateAction<string>>;
  issues: IssueFormItem[];
  onIssuesChange: (issues: IssueFormItem[]) => void;
  parties: PartyFormItem[];
  onPartiesChange: (parties: PartyFormItem[]) => void;
}

export function MatterCreatorIssuesAndContext({
  hasContextFiles,
  onSkipUpload,
  onShowUpload,
  onFilesSelected,
  isUploading,
  extractionCounts,
  showSuggestedQuestions,
  additionalContext,
  setAdditionalContext,
  issues,
  onIssuesChange,
  parties,
  onPartiesChange,
}: MatterCreatorIssuesAndContextProps) {
  const { matterId } = useParams();
  const issueTexts = issues.map((i) => i.text);
  const { data: suggestedQuestions = [], isLoading: isLoadingQuestions } = useGetSuggestedContextQuestions(
    matterId,
    showSuggestedQuestions ? issueTexts : [],
    additionalContext,
  );
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [dismissedQuestions, setDismissedQuestions] = useState<Set<number>>(new Set());
  const [loadingQuestions, setLoadingQuestions] = useState<Set<number>>(new Set());
  const formatContext = useFormatContext(matterId);

  const { t } = useTranslation();

  const submitAnswer = async (index: number) => {
    const answer = answers[index]?.trim();
    if (!answer) return;
    const question = suggestedQuestions[index];

    trackCustomEvent(TrackingEventsV2.MatterContextAnswerSuggestedQuestion, { matterId });

    setLoadingQuestions((prev) => new Set(prev).add(index));
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    try {
      const formatted = await formatContext.mutateAsync({ question, answer });
      setAdditionalContext((prev) => (prev ? `${prev}\n\n${formatted}` : formatted));
    } catch {
      setAdditionalContext((prev) => (prev ? `${prev}\n\n${answer}` : answer));
    } finally {
      setLoadingQuestions((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
      setDismissedQuestions((prev) => new Set(prev).add(index));
    }
  };

  const visibleQuestions = suggestedQuestions
    .map((q, i) => ({ question: q, index: i }))
    .filter(({ index }) => !dismissedQuestions.has(index));

  if (!matterId) return null;

  if (!hasContextFiles) {
    return (
      <ContextFileUploader
        onSkip={onSkipUpload}
        onFilesSelected={onFilesSelected}
        isUploading={isUploading}
        extractionCounts={extractionCounts}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-12 overflow-y-auto pt-5 pb-24">
      <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <Icon className="lucide--shield-alert text-amber-500" />
        Always review issues, parties, and context that are extracted using AI.
      </p>

      {/* Issues */}
      <section className="w-full px-3">
        <div className="my-8">
          <h2 className="mb-4 font-serif text-xl font-medium">Matter Issues</h2>
          <p className="text-gray-500">
            Issues are essential for Wexler to determine relevant facts that we have extracted from your documents.
            Facts and documents will also be tagged by the issues they pertain to.
          </p>
        </div>

        <IssuesList issues={issues} onChange={onIssuesChange} />
      </section>

      <hr />

      {/* Parties */}
      <section className="w-full px-3">
        <div className="my-8">
          <h2 className="mb-4 font-serif text-xl font-medium">Parties</h2>
          <p className="text-gray-500">
            Add the parties involved in this Matter. Wexler uses parties to identify relevant individuals and
            {t('organisations')} across your documents.
          </p>
        </div>

        <PartiesList parties={parties} onChange={onPartiesChange} />
      </section>

      <hr />

      {/* Additional Context */}
      <section className="w-full px-3">
        <div className="my-8">
          <h2 className="mb-4 font-serif text-xl font-medium">Additional Context</h2>
          <p className="text-gray-500">Include any other information about your Matter that may be relevant here.</p>
        </div>

        <Textarea
          className="min-h-52"
          placeholder="Type any additional context..."
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
        />

        {visibleQuestions.length > 0 && (
          <div className="mt-4">
            <p className="mb-3 text-sm font-medium text-amber-600 underline">
              Optional: You may also want to consider:
            </p>
            <ul className="space-y-4">
              {visibleQuestions.map(({ question, index }) => (
                <li key={index} className="space-y-1.5">
                  <p className="flex items-center gap-2 text-sm text-amber-600">
                    <Icon className="lucide--circle-help flex-shrink-0 text-xs" />
                    {question}
                  </p>
                  {loadingQuestions.has(index) ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Icon className="lucide--loader-2 animate-spin text-xs" />
                      Adding to context...
                    </div>
                  ) : (
                    <ButtonGroup className="w-full">
                      <Input
                        value={answers[index] ?? ''}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, [index]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            submitAnswer(index);
                          }
                        }}
                        placeholder="Type your answer..."
                        className="text-sm"
                      />
                      <Button onClick={() => submitAnswer(index)} disabled={!answers[index]?.trim()} size="sm">
                        Add
                      </Button>
                    </ButtonGroup>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isLoadingQuestions && (
          <p className="mt-4 text-xs text-gray-400">Checking for gaps in your matter context...</p>
        )}
      </section>

      <p className="text-center text-xs text-gray-500">
        <button onClick={onShowUpload} className="inline-flex items-center gap-1 text-blue-500">
          <Icon className="lucide--arrow-left" /> Back to file upload
        </button>
      </p>
    </div>
  );
}
